import {
  AGENT_SUBJECTS,
  type AgentDraft,
  type AgentDraftGoal,
  type AgentSubject,
  goalTypeSubject,
  readVocabulary,
  implicitSubjectOfVariableName,
  subjectOfVariableName,
  type TemplateVariable,
  TemplateVariableType,
} from "@/shared/domain/agents";

/**
 * Per-variable metadata the user can edit in the editor. Keyed by
 * variable name so an entry can survive a placeholder being temporarily
 * deleted and re-typed without losing the user's typed description /
 * default. Stale entries (whose name no longer matches any detected
 * placeholder) are dropped at save time by `buildVariables`.
 */
export interface VariableMeta {
  description: string;
  default: string;
}

/** Fixed description for the implicit `{{dataset.id}}` variable — the row
 *  is present whenever the agent runs on a dataset, and we don't let the
 *  user edit it because the meaning is invariant. */
export const IMPLICIT_DATASET_DESCRIPTION =
  AGENT_SUBJECTS.dataset.variableDescription;

export interface DatasetSummaryGoalState {
  goal_type: "dataset_summary";
  /** Raw textarea contents; empty string means "omit on save and use the
   *  handler's opinionated default." Validated 1-4000 chars when non-empty
   *  to mirror :class:`DatasetSummaryAgentGoal.summary_format_spec_prompt`. */
  summary_format_spec_prompt: string;
}

export interface DatasetTriageGoalState {
  goal_type: "dataset_triage";
  /** Editable list rather than a dict so the user can reorder rows and
   *  the UI can render stable keys; converted to a ``Record`` on save. */
  label_vocabulary: Array<{ label: string; description: string }>;
}

export interface CreateEventsGoalState {
  goal_type: "create_events";
  /** Fixed event types the agent may create — each row's ``name`` becomes a
   *  created event's name. Stored as an array (not a Record) so rows reorder
   *  and React keys stay stable; flattened to a Record on save. Mirrors
   *  :class:`CreateEventsGoal.event_vocabulary`. */
  event_vocabulary: Array<{ name: string; description: string }>;
  /** Fixed tags the agent may attach to created events, each with a
   *  description of when it applies. May be empty. Mirrors
   *  :class:`CreateEventsGoal.tag_vocabulary`. */
  tag_vocabulary: Array<{ tag: string; description: string }>;
  /** When true, created events are filed into a collection chosen at launch
   *  time (via the implicit ``{{collection.id}}`` variable). When false the
   *  goal carries no ``collection_id`` and events are not collected. */
  collection_enabled: boolean;
  /** Raw textarea contents; empty string means "omit on save". Layered on
   *  top of the vocabularies. Mirrors :class:`CreateEventsGoal.event_focus_prompt`. */
  event_focus_prompt: string;
}

/**
 * The session counterpart of {@link CreateEventsGoalState}: creates session
 * events on the session chosen at launch (the implicit ``{{session.id}}``
 * variable). Session events are not filed into a collection, so there is no
 * collection toggle. Mirrors :class:`SessionCreateEventsGoal`.
 */
export interface SessionCreateEventsGoalState extends Omit<
  CreateEventsGoalState,
  "goal_type" | "collection_enabled"
> {
  goal_type: "session_create_events";
}

/**
 * The session counterpart of {@link DatasetSummaryGoalState}: summarizes the
 * session chosen at launch. Mirrors :class:`SessionSummaryAgentGoal`.
 */
export interface SessionSummaryGoalState extends Omit<
  DatasetSummaryGoalState,
  "goal_type"
> {
  goal_type: "session_summary";
}

/**
 * The session counterpart of {@link DatasetTriageGoalState}: its labels become
 * tags on the session chosen at launch. Mirrors :class:`SessionTriageGoal`.
 */
export interface SessionTriageGoalState extends Omit<
  DatasetTriageGoalState,
  "goal_type"
> {
  goal_type: "session_triage";
}

export type GoalState =
  | DatasetSummaryGoalState
  | DatasetTriageGoalState
  | CreateEventsGoalState
  | SessionSummaryGoalState
  | SessionTriageGoalState
  | SessionCreateEventsGoalState;

/**
 * One org-shared skill attached to the agent. Mirrors the server's
 * ``AvailableSkillSpec``: a skill reference plus an optional pinned version.
 *
 * The skill must be one of the org-shared tiers (``org`` or ``org-editable``)
 * — both have org-wide visibility, so they resolve for every teammate who
 * launches the agent. A private skill would not.
 *
 * ``version: null`` means "track latest" — it is omitted from the wire
 * payload so the server resolves ``MAX(version)`` afresh at each invoke. A
 * numeric ``version`` pins that exact version.
 */
export interface SkillSelection {
  skill_id: string;
  version: number | null;
}

/**
 * What the editor says an agent runs on: the kind of record it is launched
 * on, or `"none"` for an agent that runs on its kickoff message alone and is
 * launched from its own page or a trigger. The server's `AgentRecord.subject`
 * has no `"none"`; it is `null` there.
 */
export type EditorSubject = AgentSubject | "none";

/**
 * Source of truth for the editor's form state.
 *
 * The editor stores everything the user can edit as plain text and
 * structured arrays here, then derives the wire-format
 * ``StartAgentSessionRequest`` and the declared-variables list at save
 * time. Keeping every text field in the form state (rather than an
 * already-built JSON dict) is what makes derived-variable detection
 * possible without re-parsing the body.
 */
export interface FormState {
  name: string;
  description: string;
  /**
   * What the agent runs on, which decides the goals the editor offers. Every
   * editable goal works on this kind of record, and an agent on `"none"` has
   * no goals. `null` until a person creating an agent picks one.
   */
  subject: EditorSubject | null;
  modelProfile: string | null;
  kickoffMessage: string;
  goals: GoalState[];
  /** Org-shared skills the agent's AI may invoke. Order is preserved on the
   *  wire; an empty list is meaningful (see `buildRequestTemplate`). */
  skills: SkillSelection[];
  /** User-editable description / default for each variable, keyed by name.
   *  The implicit `dataset.id` and `session.id` rows are never kept here
   *  (see IMPLICIT_DATASET_DESCRIPTION and IMPLICIT_SESSION_DESCRIPTION).
   *  Stale keys (variables no longer referenced by any placeholder) are
   *  dropped on save. */
  variableMeta: Record<string, VariableMeta>;
  /**
   * Saved goals of a type the editor cannot edit, such as one added to the
   * SDK after this build. Written back on save exactly as they were read,
   * at their saved positions, so saving an agent never deletes or reorders
   * one.
   */
  passthroughGoals: PassthroughGoal[];
  /**
   * Saved variable declarations the editor cannot produce: those a
   * passthrough goal names, and those whose type differs from the one the
   * editor gives that name (see {@link editorVariableType}), such as a
   * custom-named dataset variable. Shown read-only in the Variables panel and
   * written back verbatim while still named.
   */
  passthroughVariables: TemplateVariable[];
  /**
   * The saved ``client_context`` minus the ``{{dataset.id}}`` and
   * ``{{session.id}}`` placeholders, which a save writes back only as the
   * subject binding of {@link subjectBoundByClientContext}, and with empty
   * lists dropped. The rest is written back unchanged, so a save keeps
   * context set through the SDK or API. Empty when there is none.
   */
  passthroughClientContext: Record<string, unknown>;
  /**
   * Whether the saved ``client_context`` named a ``{{dataset.id}}`` or
   * ``{{session.id}}`` placeholder, which ``passthroughClientContext`` leaves
   * out. A save then names the current subject's placeholder again, even when
   * the agent has goals, so a placeholder set through the SDK or API survives
   * a save (and follows a switch between dataset and session). Absent means
   * false.
   */
  subjectInClientContext?: boolean;
}

/** A saved goal the editor cannot edit, and where it sat in the saved list. */
export interface PassthroughGoal {
  /** Index in the saved ``goals`` list, so a save puts the goal back there. */
  savedIndex: number;
  goal: Record<string, unknown>;
}

/** The implicit, undeletable variable name injected at save time into every
 *  dataset goal, or into the client context of a goal-less agent on a
 *  dataset. Mirrored as a synthetic row in the variables panel. */
export const IMPLICIT_DATASET_VAR_NAME = AGENT_SUBJECTS.dataset.variableName;

/** Placeholder that replaces a dataset goal's ``dataset_id`` field. */
export const IMPLICIT_DATASET_PLACEHOLDER = AGENT_SUBJECTS.dataset.placeholder;

/** Fixed description for the implicit `{{collection.id}}` variable — present
 *  whenever a create-events goal is declared, and not user-editable because
 *  the meaning is invariant. */
export const IMPLICIT_COLLECTION_DESCRIPTION =
  "The collection created events are added to. Selected at launch time.";

/** The implicit, undeletable variable name injected into every create-events
 *  goal at save time. Mirrored as a synthetic row in the variables panel. */
export const IMPLICIT_COLLECTION_VAR_NAME = "collection.id";

/** Placeholder that replaces a create-events goal's ``collection_id`` field. */
export const IMPLICIT_COLLECTION_PLACEHOLDER = `{{${IMPLICIT_COLLECTION_VAR_NAME}}}`;

/** Fixed description for the implicit `{{session.id}}` variable — present
 *  whenever the agent runs on a session, and not user-editable because the
 *  meaning is invariant. */
export const IMPLICIT_SESSION_DESCRIPTION =
  AGENT_SUBJECTS.session.variableDescription;

/** The implicit, undeletable variable name injected at save time into every
 *  session goal, or into the client context of a goal-less agent on a
 *  session. Mirrored as a synthetic row in the variables panel. */
export const IMPLICIT_SESSION_VAR_NAME = AGENT_SUBJECTS.session.variableName;

/** Placeholder that replaces a session goal's ``session_id`` field. */
export const IMPLICIT_SESSION_PLACEHOLDER = AGENT_SUBJECTS.session.placeholder;

/**
 * `goal` as the goal of the same kind on `subject` (a summary stays a summary,
 * a triage a triage, an events goal an events goal), keeping everything the
 * two share. Only a dataset events goal's collection has no session
 * counterpart: session events are not filed into a collection.
 */
export function goalForSubject(
  goal: GoalState,
  subject: AgentSubject,
): GoalState {
  const onDataset = subject === "dataset";
  switch (goal.goal_type) {
    case "dataset_summary":
    case "session_summary":
      return {
        goal_type: onDataset ? "dataset_summary" : "session_summary",
        summary_format_spec_prompt: goal.summary_format_spec_prompt,
      };
    case "dataset_triage":
    case "session_triage":
      return {
        goal_type: onDataset ? "dataset_triage" : "session_triage",
        label_vocabulary: goal.label_vocabulary,
      };
    case "create_events":
    case "session_create_events": {
      const shared = {
        event_vocabulary: goal.event_vocabulary,
        tag_vocabulary: goal.tag_vocabulary,
        event_focus_prompt: goal.event_focus_prompt,
      };
      if (!onDataset) {
        return { goal_type: "session_create_events", ...shared };
      }
      return {
        goal_type: "create_events",
        ...shared,
        collection_enabled:
          goal.goal_type === "create_events" && goal.collection_enabled,
      };
    }
  }
}

/** Mirror of the server-side ``_PLACEHOLDER_RE`` so the form and the
 *  resolver agree on which substrings count as a variable reference. */
const PLACEHOLDER_RE = /\{\{\s*([a-zA-Z_][a-zA-Z0-9_.]*)\s*\}\}/g;

/**
 * Returns true when at least one declared goal carries an implicit
 * collection slot: a dataset create-events goal with its collection enabled.
 */
export function anyGoalUsesImplicitCollection(goals: GoalState[]): boolean {
  return goals.some(
    (g) => g.goal_type === "create_events" && g.collection_enabled,
  );
}

function pushPlaceholders(text: string, found: Set<string>): void {
  for (const match of text.matchAll(PLACEHOLDER_RE)) {
    found.add(match[1]);
  }
}

/** Every placeholder in the string values of a JSON value, keys excluded, as
 *  the server walks a request template. */
function pushValuePlaceholders(value: unknown, found: Set<string>): void {
  if (typeof value === "string") {
    pushPlaceholders(value, found);
  } else if (Array.isArray(value)) {
    for (const item of value) {
      pushValuePlaceholders(item, found);
    }
  } else if (typeof value === "object" && value !== null) {
    for (const item of Object.values(value)) {
      pushValuePlaceholders(item, found);
    }
  }
}

/**
 * The type {@link buildVariables} declares a variable of this name with, on an
 * agent that runs on `agentSubject`: the variable of the record the agent runs
 * on and the implicit collection carry their id types, and every other name,
 * including the other subject's ``dataset.id`` or ``session.id``, is a string.
 */
function editorVariableType(
  name: string,
  agentSubject: EditorSubject | null,
): TemplateVariableType {
  const subject = implicitSubjectOfVariableName(name, agentSubject);
  if (subject !== undefined) {
    return AGENT_SUBJECTS[subject].variableType;
  }
  if (name === IMPLICIT_COLLECTION_VAR_NAME) {
    return TemplateVariableType.COLLECTION_ID;
  }
  return TemplateVariableType.STRING;
}

/**
 * The record a goal-less agent names in its client context, or `null` when
 * the agent needs no such binding.
 *
 * The server decides what an agent without goals runs on from the types of
 * its variables, and a save declares only the variables that the template
 * names. A goal-less agent on a dataset or a session therefore names the
 * implicit variable in ``client_context``; the server also lists each thread
 * the agent starts on that record's page. Two kinds of agent need no binding:
 * - an agent with goals, because each goal names the record itself;
 * - an agent whose text already names a kept variable of the record's type.
 */
function subjectBoundByClientContext(
  formState: FormState,
): AgentSubject | null {
  const subject = formState.subject;
  if (
    subject === null ||
    subject === "none" ||
    formState.goals.length + formState.passthroughGoals.length > 0
  ) {
    return null;
  }
  const written = collectWrittenPlaceholders(formState);
  const alreadyBound = formState.passthroughVariables.some(
    (variable) =>
      variable.type === AGENT_SUBJECTS[subject].variableType &&
      written.has(variable.name),
  );
  return alreadyBound ? null : subject;
}

/**
 * The ``client_context`` a save writes: the kept context, plus the subject
 * binding of {@link subjectBoundByClientContext}. `undefined` when empty.
 */
function buildClientContext(
  formState: FormState,
): Record<string, unknown> | undefined {
  const context = { ...formState.passthroughClientContext };
  const bound =
    subjectBoundByClientContext(formState) ?? keptSubjectBinding(formState);
  if (bound !== null) {
    const { clientContextKey: key, placeholder } = AGENT_SUBJECTS[bound];
    const ids = Array.isArray(context[key]) ? (context[key] as unknown[]) : [];
    context[key] = ids.includes(placeholder) ? ids : [...ids, placeholder];
  }
  return Object.keys(context).length > 0 ? context : undefined;
}

/**
 * The subject whose placeholder a saved ``client_context`` named and a save
 * names again ({@link FormState.subjectInClientContext}), or `null`.
 */
function keptSubjectBinding(formState: FormState): AgentSubject | null {
  const subject = formState.subject;
  return formState.subjectInClientContext === true &&
    (subject === "dataset" || subject === "session")
    ? subject
    : null;
}

/** Whether a saved ``client_context`` names a subject placeholder. */
function namesSubjectInClientContext(body: Record<string, unknown>): boolean {
  const raw = body.client_context;
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) {
    return false;
  }
  const subjectPlaceholders = new Set<unknown>(
    Object.values(AGENT_SUBJECTS).map(({ placeholder }) => placeholder),
  );
  return Object.values(raw as Record<string, unknown>).some(
    (value) =>
      Array.isArray(value) &&
      value.some((item: unknown) => subjectPlaceholders.has(item)),
  );
}

/**
 * The saved ``client_context`` without what {@link buildClientContext} adds
 * on its own: the subject placeholders, and the empty lists a server
 * re-serializing the template fills in. What is left is kept as saved.
 */
function parseClientContext(
  body: Record<string, unknown>,
): Record<string, unknown> {
  const raw = body.client_context;
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) {
    return {};
  }
  const subjectPlaceholders = new Set(
    Object.values(AGENT_SUBJECTS).map(({ placeholder }) => placeholder),
  );
  const kept: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (value === null) {
      continue;
    }
    if (Array.isArray(value)) {
      const rest = value.filter(
        (item) => !(typeof item === "string" && subjectPlaceholders.has(item)),
      );
      if (rest.length > 0) {
        kept[key] = rest;
      }
      continue;
    }
    kept[key] = value;
  }
  return kept;
}

/**
 * Walks every text-bearing field in the form state and returns the set of
 * ``{{name}}`` placeholders. Mirrors the server's body walk but operates
 * on the structured form rather than the serialized JSON, so the variables
 * panel can update on every keystroke without re-running
 * ``buildRequestTemplate``. Passthrough goals are walked as saved, so the
 * panel lists the variables they name and a save declares them.
 *
 * Identity fields (``name``, ``description``) are NOT walked because
 * they're agent metadata, not part of the request body that the resolver
 * sees. ``modelProfile`` is also skipped — it's a profile id selector,
 * not freeform text. Label-vocabulary *keys* are also skipped to match
 * the server's "values only" walk, which exists so a placeholder in a
 * key can't silently collide with another key after substitution.
 */
export function collectPlaceholders(formState: FormState): Set<string> {
  const found = collectWrittenPlaceholders(formState);
  const bound =
    subjectBoundByClientContext(formState) ?? keptSubjectBinding(formState);
  if (bound !== null) {
    found.add(AGENT_SUBJECTS[bound].variableName);
  }
  return found;
}

/**
 * {@link collectPlaceholders} without the subject binding a goal-less agent
 * carries in its client context, which depends on this set.
 */
function collectWrittenPlaceholders(formState: FormState): Set<string> {
  const found = new Set<string>();
  pushPlaceholders(formState.kickoffMessage, found);
  pushValuePlaceholders(formState.passthroughClientContext, found);
  for (const { goal } of formState.passthroughGoals) {
    pushValuePlaceholders(goal, found);
  }
  for (const goal of formState.goals) {
    // Every goal names the record it works on through that record's implicit
    // variable.
    found.add(AGENT_SUBJECTS[goalTypeSubject(goal.goal_type)].variableName);
    if (
      goal.goal_type === "dataset_summary" ||
      goal.goal_type === "session_summary"
    ) {
      pushPlaceholders(goal.summary_format_spec_prompt, found);
    } else if (
      goal.goal_type === "dataset_triage" ||
      goal.goal_type === "session_triage"
    ) {
      for (const row of goal.label_vocabulary) {
        pushPlaceholders(row.description, found);
      }
    } else {
      pushPlaceholders(goal.event_focus_prompt, found);
      for (const row of goal.event_vocabulary) {
        pushPlaceholders(row.description, found);
      }
      for (const row of goal.tag_vocabulary) {
        pushPlaceholders(row.description, found);
      }
    }
  }
  if (anyGoalUsesImplicitCollection(formState.goals)) {
    found.add(IMPLICIT_COLLECTION_VAR_NAME);
  }
  return found;
}

/**
 * Converts the placeholder set into a list of ``TemplateVariable``
 * declarations ready for the create / update wire payload. The
 * implicit dataset and session rows get their id types
 * (:data:`TemplateVariableType.DATASET_ID`, :data:`TemplateVariableType.SESSION_ID`)
 * and a fixed description; user-introduced variables get
 * :data:`TemplateVariableType.STRING` and pick up the editor-supplied
 * description / default from `variableMeta` (empty strings collapse to
 * `null` so they're omitted on the wire). A name in `passthroughVariables`
 * is written as it was saved instead.
 *
 * The output is sorted so commits and snapshots are stable.
 */
export function buildVariables(formState: FormState): TemplateVariable[] {
  const keptByName = new Map(
    formState.passthroughVariables.map((variable) => [variable.name, variable]),
  );
  const placeholders = collectPlaceholders(formState);
  return [...placeholders].sort().map((name): TemplateVariable => {
    const kept = keptByName.get(name);
    if (kept !== undefined) {
      return kept;
    }
    const subject = implicitSubjectOfVariableName(name, formState.subject);
    if (subject !== undefined) {
      const { variableType, variableDescription } = AGENT_SUBJECTS[subject];
      return {
        name,
        type: variableType,
        description: variableDescription,
        required: true,
        default: null,
      };
    }
    if (name === IMPLICIT_COLLECTION_VAR_NAME) {
      // The author may pre-fill a default collection so the launch page
      // doesn't force a picker click every time. The description is still
      // fixed — see IMPLICIT_COLLECTION_DESCRIPTION — because it explains
      // the variable's meaning, not its current value.
      const meta = formState.variableMeta[name];
      const defaultValue = meta?.default ?? "";
      return {
        name,
        type: TemplateVariableType.COLLECTION_ID,
        // The create-events goal only ever files into event collections, so
        // the launch-page picker is hard-filtered to that resource type.
        collection_content_type: "event",
        description: IMPLICIT_COLLECTION_DESCRIPTION,
        required: true,
        default: defaultValue === "" ? null : defaultValue,
      };
    }
    const meta = formState.variableMeta[name];
    const description = meta?.description.trim() ?? "";
    const defaultValue = meta?.default ?? "";
    return {
      name,
      type: TemplateVariableType.STRING,
      description: description === "" ? null : description,
      required: true,
      default: defaultValue === "" ? null : defaultValue,
    };
  });
}

type RequestTemplateJson = Record<string, unknown>;

/** The vocabularies and focus both events goals carry, as the wire writes them. */
function eventsGoalFieldsJson(
  goal: CreateEventsGoalState | SessionCreateEventsGoalState,
): Record<string, unknown> {
  const eventVocabulary: Record<string, string> = {};
  for (const row of goal.event_vocabulary) {
    if (row.name !== "") {
      eventVocabulary[row.name] = row.description;
    }
  }
  const fields: Record<string, unknown> = { event_vocabulary: eventVocabulary };
  const tagVocabulary: Record<string, string> = {};
  for (const row of goal.tag_vocabulary) {
    if (row.tag !== "") {
      tagVocabulary[row.tag] = row.description;
    }
  }
  // Omit an empty tag_vocabulary — the server defaults it to {} — so a
  // parse → build → parse round-trip on a goal with no tags is stable.
  if (Object.keys(tagVocabulary).length > 0) {
    fields.tag_vocabulary = tagVocabulary;
  }
  if (goal.event_focus_prompt.trim().length > 0) {
    fields.event_focus_prompt = goal.event_focus_prompt;
  }
  return fields;
}

/** The field a goal names its record by, and the placeholder bound to it. */
function subjectBindingJson(goal: GoalState): Record<string, string> {
  const { goalField, placeholder } =
    AGENT_SUBJECTS[goalTypeSubject(goal.goal_type)];
  return { [goalField]: placeholder };
}

function buildGoalJson(goal: GoalState): Record<string, unknown> {
  if (
    goal.goal_type === "dataset_summary" ||
    goal.goal_type === "session_summary"
  ) {
    const summary: Record<string, unknown> = {
      goal_type: goal.goal_type,
      ...subjectBindingJson(goal),
    };
    const trimmed = goal.summary_format_spec_prompt.trim();
    if (trimmed.length > 0) {
      summary.summary_format_spec_prompt = goal.summary_format_spec_prompt;
    }
    return summary;
  }
  if (
    goal.goal_type === "create_events" ||
    goal.goal_type === "session_create_events"
  ) {
    const createEvents: Record<string, unknown> = {
      goal_type: goal.goal_type,
      ...subjectBindingJson(goal),
      ...eventsGoalFieldsJson(goal),
    };
    if (goal.goal_type === "create_events" && goal.collection_enabled) {
      createEvents.collection_id = IMPLICIT_COLLECTION_PLACEHOLDER;
    }
    return createEvents;
  }
  const vocabulary: Record<string, string> = {};
  for (const row of goal.label_vocabulary) {
    if (row.label !== "") {
      vocabulary[row.label] = row.description;
    }
  }
  return {
    goal_type: goal.goal_type,
    ...subjectBindingJson(goal),
    label_vocabulary: vocabulary,
  };
}

/**
 * Builds the request-body half of the wire payload: a JSON dict shaped
 * like :class:`StartAgentSessionRequest`. Omits every field the server
 * defaults sensibly (``system_prompt``, ``client_tools``,
 * ``analysis_scope``, ``visibility``) so the editor never has to reason
 * about them. ``client_context`` is written only when it carries something:
 * context kept from the saved agent, or the record a goal-less agent runs on
 * (see {@link subjectBoundByClientContext}).
 *
 * ``available_skills`` is the deliberate exception — it is written
 * unconditionally, including as an empty ``[]``. An agent is a shared,
 * reproducible workflow, so its AI-invokable skill set must be explicit:
 * ``[]`` means "no skills" deterministically for every invoker. Omitting
 * the field (the server's ``null`` default) would instead fall back to the
 * invoking user's personal skill subscriptions — non-deterministic and
 * surprising for a shared agent.
 *
 * Returned value is a plain object suitable for ``JSON.stringify``;
 * callers should not mutate it.
 */
export function buildRequestTemplate(
  formState: FormState,
): RequestTemplateJson {
  const body: RequestTemplateJson = {
    goals: interleavePassthroughGoals(
      formState.goals.map(buildGoalJson),
      formState.passthroughGoals,
    ),
    available_skills: formState.skills.map((skill) =>
      skill.version === null
        ? { skill_id: skill.skill_id }
        : { skill_id: skill.skill_id, version: skill.version },
    ),
    // Server-side `AgentTextContent` has only a `text` field — no
    // discriminator — so we send the bare shape rather than a fake
    // `type: "text"` tag the server ignores and that we'd then fail to
    // round-trip in `parseKickoffMessage`.
    messages:
      formState.kickoffMessage.length > 0
        ? [
            {
              role: "user",
              content: [{ text: formState.kickoffMessage }],
            },
          ]
        : [],
  };
  if (formState.modelProfile) {
    body.model_profile = formState.modelProfile;
  }
  const clientContext = buildClientContext(formState);
  if (clientContext !== undefined) {
    body.client_context = clientContext;
  }
  return body;
}

/**
 * The editable goals with each passthrough goal put back at its saved index,
 * so a save keeps the saved order (it decides each goal's ``goal_index`` and
 * the order a thread shows them in). An index past the end, left by removing
 * editable goals, appends the goal instead.
 */
function interleavePassthroughGoals(
  editable: Record<string, unknown>[],
  passthrough: readonly PassthroughGoal[],
): Record<string, unknown>[] {
  const goals = [...editable];
  const bySavedIndex = [...passthrough].sort(
    (a, b) => a.savedIndex - b.savedIndex,
  );
  for (const { savedIndex, goal } of bySavedIndex) {
    goals.splice(savedIndex, 0, goal);
  }
  return goals;
}

interface SavedAgent {
  name: string;
  description?: string | null;
  request_template: Record<string, unknown>;
  variables: TemplateVariable[];
  subject?: AgentSubject | null;
}

/**
 * Best-effort reverse of :func:`buildRequestTemplate` for edit mode.
 *
 * Fields outside the editor's shape (legacy fields, future fields we don't
 * handle) are dropped. Goals, variables and client context the editor cannot
 * produce are the exception: a goal of a type it cannot edit, such as one
 * added to the SDK after this build, is kept in ``passthroughGoals``, the
 * declarations in ``passthroughVariables``, and the client context in
 * ``passthroughClientContext``, so a save writes them back unchanged instead
 * of deleting or retyping them.
 */
export function parseSavedAgent(saved: SavedAgent): FormState {
  const body = saved.request_template;
  // An agent the server places on nothing runs on nothing specific.
  const subject: EditorSubject = saved.subject ?? "none";
  const { goals, passthroughGoals } = parseGoals(body);
  return {
    name: saved.name,
    description: saved.description ?? "",
    subject,
    modelProfile: parseModelProfile(body),
    kickoffMessage: parseKickoffMessage(body),
    goals,
    skills: parseSkills(body),
    variableMeta: parseVariableMeta(saved.variables, subject),
    passthroughGoals,
    passthroughVariables: parsePassthroughVariables(
      saved.variables,
      passthroughGoals,
      subject,
    ),
    passthroughClientContext: parseClientContext(body),
    // A goal-less agent's binding is derived on save; one beside goals is kept.
    ...(goals.length + passthroughGoals.length > 0 &&
      namesSubjectInClientContext(body) && { subjectInClientContext: true }),
  };
}

/**
 * The saved variables {@link buildVariables} would not write back as they
 * are: those a passthrough goal names, whatever their type, and those whose
 * type is not the one {@link editorVariableType} gives their name.
 */
function parsePassthroughVariables(
  variables: readonly TemplateVariable[],
  passthroughGoals: readonly PassthroughGoal[],
  agentSubject: EditorSubject,
): TemplateVariable[] {
  const named = new Set<string>();
  for (const { goal } of passthroughGoals) {
    pushValuePlaceholders(goal, named);
  }
  return variables.filter(
    (variable) =>
      named.has(variable.name) ||
      (variable.type !== editorVariableType(variable.name, agentSubject) &&
        !isRetiredSubjectVariable(variable)),
  );
}

/**
 * Whether `variable` is a subject variable the editor declared with its id
 * type while the agent ran on that subject, such as the ``session.id`` of an
 * agent since switched to datasets. The editor wrote it, so it is not kept
 * read-only; a save declares it again as an ordinary string variable.
 */
function isRetiredSubjectVariable(variable: TemplateVariable): boolean {
  const subject = subjectOfVariableName(variable.name);
  return (
    subject !== undefined &&
    variable.type === AGENT_SUBJECTS[subject].variableType
  );
}

/**
 * Reverse of the ``available_skills`` half of {@link buildRequestTemplate}.
 *
 * Reads ``request_template.available_skills`` — a list of
 * ``{ skill_id, version? }`` — into {@link SkillSelection} rows. A missing or
 * non-array value (a legacy agent saved before the Skills section existed, or
 * the server's ``null`` default) yields an empty list; re-saving that agent
 * through the editor then writes an explicit ``[]``. Entries without a
 * string ``skill_id`` are dropped, and a non-numeric ``version`` collapses to
 * ``null`` (track latest).
 */
function parseSkills(body: Record<string, unknown>): SkillSelection[] {
  const available = body.available_skills;
  if (!Array.isArray(available)) {
    return [];
  }
  const parsed: SkillSelection[] = [];
  for (const raw of available) {
    if (typeof raw !== "object" || raw === null) {
      continue;
    }
    const obj = raw as Record<string, unknown>;
    if (typeof obj.skill_id !== "string") {
      continue;
    }
    parsed.push({
      skill_id: obj.skill_id,
      version: typeof obj.version === "number" ? obj.version : null,
    });
  }
  return parsed;
}

/**
 * Build a {@link VariableMeta} entry from raw description + default
 * strings, applying the trim-then-collapse rule both code paths into
 * ``variableMeta`` share: trim the description, treat empty / missing
 * defaults as no default declared, and return ``null`` when the entry
 * carries no user-supplied content so the surrounding loops can skip it.
 *
 * Centralising this rule keeps every path into ``variableMeta`` producing the
 * same shape for the same logical agent: edit-mode ({@link parseSavedAgent}),
 * create-from-draft ({@link draftToFormState}), and the AI update handoff —
 * all of which reach it through {@link parseVariableMeta}. Without it, e.g. a
 * saved agent with a whitespace-only description survived edit-mode parse
 * while the equivalent draft was dropped — different shapes for the same
 * content.
 */
function normalizeVariableMeta(
  description: string | null | undefined,
  defaultValue: string | null | undefined,
): VariableMeta | null {
  const trimmedDescription = (description ?? "").trim();
  const value = defaultValue ?? "";
  if (trimmedDescription === "" && value === "") {
    return null;
  }
  return { description: trimmedDescription, default: value };
}

/**
 * Builds the editor's ``variableMeta`` map from declared variables.
 *
 * The parameter is the structural subset shared by a saved agent's
 * {@link TemplateVariable} and a drafted `AgentDraftVariable` (not imported
 * here — the structural type is the contract, not either nominal one), so the
 * edit-mode parse and both draft handoffs produce identical shapes for the
 * same variables — the divergence {@link normalizeVariableMeta} exists to
 * prevent.
 */
function parseVariableMeta(
  variables: readonly {
    name: string;
    description?: string | null;
    default?: string | null;
  }[],
  agentSubject: EditorSubject | null,
): Record<string, VariableMeta> {
  const meta: Record<string, VariableMeta> = {};
  for (const variable of variables) {
    // The variable of the record the agent runs on has no user-editable
    // metadata (no default either — it is always chosen at launch time), so
    // skip it outright.
    if (
      implicitSubjectOfVariableName(variable.name, agentSubject) !== undefined
    ) {
      continue;
    }
    const otherSubject = subjectOfVariableName(variable.name);
    if (otherSubject !== undefined) {
      // The other subject's variable is the author's to describe. A
      // description that is still the fixed one it carried while the agent ran
      // on that subject was never theirs, so it is not kept.
      const description =
        variable.description ===
        AGENT_SUBJECTS[otherSubject].variableDescription
          ? null
          : variable.description;
      const entry = normalizeVariableMeta(description, variable.default);
      if (entry !== null) {
        meta[variable.name] = entry;
      }
      continue;
    }
    if (variable.name === IMPLICIT_COLLECTION_VAR_NAME) {
      // The collection's description is fixed (IMPLICIT_COLLECTION_DESCRIPTION),
      // but the author may pre-fill a default collection that the launch
      // page surfaces. Preserve the default; ignore any persisted description.
      const defaultValue = variable.default ?? "";
      if (defaultValue !== "") {
        meta[variable.name] = { description: "", default: defaultValue };
      }
      continue;
    }
    const entry = normalizeVariableMeta(variable.description, variable.default);
    if (entry !== null) {
      meta[variable.name] = entry;
    }
  }
  return meta;
}

function parseModelProfile(body: Record<string, unknown>): string | null {
  const value = body.model_profile;
  return typeof value === "string" && value.length > 0 ? value : null;
}

function parseKickoffMessage(body: Record<string, unknown>): string {
  const messages = body.messages;
  if (!Array.isArray(messages) || messages.length === 0) {
    return "";
  }
  const first = messages[0] as Record<string, unknown> | undefined;
  if (first === undefined || !Array.isArray(first.content)) {
    return "";
  }
  // Server-side `AgentTextContent` is identified by the absence of a
  // `content_type` discriminator on its sibling content types. Pick the
  // first block that has a string `text` field and no other content type
  // tag — this is the canonical text block shape the SDK emits.
  for (const block of first.content) {
    if (typeof block !== "object" || block === null) {
      continue;
    }
    const record = block as Record<string, unknown>;
    if (record.content_type === undefined && typeof record.text === "string") {
      return record.text;
    }
  }
  return "";
}

/**
 * A blank editor form — the starting point for a create, and the base the
 * create handoff overlays a draft onto.
 *
 * A factory rather than a shared constant so no two forms alias the same
 * ``goals`` / ``skills`` arrays.
 */
export function emptyFormState(): FormState {
  return {
    name: "",
    description: "",
    // Unset: a person creating an agent picks what it runs on first.
    subject: null,
    modelProfile: null,
    kickoffMessage: "",
    goals: [],
    skills: [],
    variableMeta: {},
    passthroughGoals: [],
    passthroughVariables: [],
    passthroughClientContext: {},
  };
}

/** What the editor last seeded its form from. */
export interface SeededFrom {
  agentId: string;
  draft: AgentDraft | null;
}

/**
 * Whether the edit form should be (re)seeded from ``agentId``'s saved values
 * overlaid with ``draft``, given what it was ``seeded`` from last.
 *
 * Seeding is one-shot per (agent, proposal) pair rather than per agent. The
 * per-agent half is what keeps a refetch — a post-save cache invalidation, a
 * tab-focus refresh — from throwing away edits the user is in the middle of
 * typing. The per-proposal half is what lets a *second* AI proposal land: when
 * the AI updates the agent the user already has open, neither the route nor
 * the fetched agent changes, so an agent-only gate reads the overlay as
 * already applied and the form never moves.
 *
 * Identity, not value, is the test on both: a claimed draft keeps one
 * reference for as long as the claim is held (see ``useDraftSeededForm``), so
 * a re-render carrying the same proposal is a no-op while a freshly claimed
 * one always re-seeds.
 */
export function shouldSeedForm(
  seeded: SeededFrom | null,
  agentId: string,
  draft: AgentDraft | null,
): boolean {
  return (
    seeded === null || seeded.agentId !== agentId || seeded.draft !== draft
  );
}

/**
 * Overlays an {@link AgentDraft} onto an existing {@link FormState}.
 *
 * ``base`` is the form to merge into — a saved agent's form (via
 * {@link parseSavedAgent}) for the update handoff, or
 * {@link emptyFormState} for a create. A field the draft PROVIDES
 * full-replaces the base value; a field it OMITS keeps the base value.
 * ``goals`` and ``variables`` replace the editable goals and the variable
 * metadata when provided. ``passthroughGoals`` and ``passthroughVariables``
 * always survive from ``base``: a draft can propose only the goal kinds the
 * editor edits, so it has no way to restate or remove a goal it cannot see.
 *
 * This is also where the draft's wire representation is translated, so the
 * editor never peeks at it (notably ``label_vocabulary`` is a ``Record`` in
 * the draft and an ordered array in the form). Variables naming the
 * implicit dataset or session placeholder are dropped by
 * {@link parseVariableMeta}, because {@link buildVariables} injects those rows
 * automatically and an LLM-supplied entry would duplicate one or fight its
 * fixed description ({@link IMPLICIT_DATASET_DESCRIPTION},
 * {@link IMPLICIT_SESSION_DESCRIPTION}).
 *
 * ``skills`` carry resolved ``skill_id``s — the tool resolves the names the
 * model supplies before stashing, so an unknown or private skill fails as a
 * tool error rather than arriving here unresolvable. Omitting the field keeps
 * the saved agent's skills; an explicit ``[]`` detaches them all. The human
 * reviews and saves the merged form — this only stages the overlay.
 */
export function overlayDraftOnForm(
  base: FormState,
  draft: AgentDraft,
): FormState {
  const goals =
    draft.goals === undefined
      ? base.goals
      : draft.goals.map(draftGoalToFormGoal);
  // The agent runs on what its first goal works on. `client_create_agent`
  // refuses a draft whose goals mix datasets and sessions
  // (`mixedSubjectRefusalFor`), so every drafted goal agrees.
  const subject =
    goals.length > 0 ? goalTypeSubject(goals[0].goal_type) : base.subject;
  return {
    ...base,
    subject,
    name: draft.name ?? base.name,
    description: draft.description ?? base.description,
    modelProfile: draft.model_profile ?? base.modelProfile,
    kickoffMessage: draft.kickoff_message ?? base.kickoffMessage,
    // Both list fields test `undefined` explicitly, so "provided" means the
    // same thing for each. An explicitly-empty `goals: []` / `variables: []`
    // is provided, and clears the base's editable goals / variable metadata.
    goals,
    variableMeta:
      draft.variables === undefined
        ? base.variableMeta
        : parseVariableMeta(draft.variables, subject),
    skills:
      draft.skills === undefined
        ? base.skills
        : draft.skills.map((skill) => ({
            skill_id: skill.skill_id,
            version: skill.version ?? null,
          })),
  };
}

/**
 * Maps an {@link AgentDraft} stashed by the ``client_create_agent`` client
 * tool into a fresh {@link FormState} for the create handoff.
 */
export function draftToFormState(draft: AgentDraft): FormState {
  return overlayDraftOnForm(emptyFormState(), draft);
}

function draftGoalToFormGoal(goal: AgentDraftGoal): GoalState {
  if (
    goal.goal_type === "dataset_summary" ||
    goal.goal_type === "session_summary"
  ) {
    return {
      goal_type: goal.goal_type,
      summary_format_spec_prompt: goal.summary_format_spec_prompt ?? "",
    };
  }
  if (
    goal.goal_type === "create_events" ||
    goal.goal_type === "session_create_events"
  ) {
    // Both vocabularies are Records on the wire (so the LLM can emit them
    // as plain JSON objects); the editor stores them as ordered arrays
    // for stable React keys and reordering. `consumeAgentDraft` already
    // drops an events goal whose `event_vocabulary` isn't a plain object,
    // so the `?? {}` is defense-in-depth for the same reason as the triage
    // branch below.
    const shared = {
      event_vocabulary: Object.entries(goal.event_vocabulary ?? {}).map(
        ([name, description]) => ({ name, description }),
      ),
      tag_vocabulary: Object.entries(goal.tag_vocabulary ?? {}).map(
        ([tag, description]) => ({ tag, description }),
      ),
      event_focus_prompt: goal.event_focus_prompt ?? "",
    };
    if (goal.goal_type === "session_create_events") {
      return { goal_type: "session_create_events", ...shared };
    }
    return {
      goal_type: "create_events",
      ...shared,
      collection_enabled: goal.collection_enabled === true,
    };
  }
  // A triage goal, of a dataset or a session: the draft's vocabulary is a
  // Record so the LLM can emit it as a plain JSON object. The editor stores
  // rows as an array to preserve order and stable React keys, so we flatten
  // here.
  //
  // Defensive `?? {}`: `consumeAgentDraft` already drops triage goals
  // whose `label_vocabulary` isn't a plain object, so we should never
  // see a malformed value here. The fallback exists in case a future
  // producer bypasses the validator and writes a draft directly — the
  // editor's `useState` initializer is a bad place to throw because
  // there's no error boundary above it.
  return {
    goal_type: goal.goal_type,
    label_vocabulary: Object.entries(goal.label_vocabulary ?? {}).map(
      ([label, description]) => ({ label, description }),
    ),
  };
}

/**
 * Splits the saved goals into those the editor can edit and those it keeps
 * as saved. A goal whose ``goal_type`` no branch below reads is a
 * passthrough goal, so the two lists never share a goal.
 */
function parseGoals(body: Record<string, unknown>): {
  goals: GoalState[];
  passthroughGoals: PassthroughGoal[];
} {
  const goals = body.goals;
  if (!Array.isArray(goals)) {
    return { goals: [], passthroughGoals: [] };
  }
  const parsed: GoalState[] = [];
  const passthroughGoals: PassthroughGoal[] = [];
  for (const [savedIndex, raw] of goals.entries()) {
    if (typeof raw !== "object" || raw === null) {
      continue;
    }
    const obj = raw as Record<string, unknown>;
    if (
      obj.goal_type === "dataset_summary" ||
      obj.goal_type === "session_summary"
    ) {
      parsed.push({
        goal_type: obj.goal_type,
        summary_format_spec_prompt:
          typeof obj.summary_format_spec_prompt === "string"
            ? obj.summary_format_spec_prompt
            : "",
      });
    } else if (
      obj.goal_type === "dataset_triage" ||
      obj.goal_type === "session_triage"
    ) {
      parsed.push({
        goal_type: obj.goal_type,
        label_vocabulary: Object.entries(
          readVocabulary(obj.label_vocabulary),
        ).map(([label, description]) => ({ label, description })),
      });
    } else if (
      obj.goal_type === "create_events" ||
      obj.goal_type === "session_create_events"
    ) {
      // Both vocabularies are Records on the wire; the editor stores them as
      // ordered arrays for stable React keys and reordering. Flatten here.
      const shared = {
        event_vocabulary: Object.entries(
          readVocabulary(obj.event_vocabulary),
        ).map(([name, description]) => ({ name, description })),
        tag_vocabulary: Object.entries(readVocabulary(obj.tag_vocabulary)).map(
          ([tag, description]) => ({ tag, description }),
        ),
        event_focus_prompt:
          typeof obj.event_focus_prompt === "string"
            ? obj.event_focus_prompt
            : "",
      };
      parsed.push(
        obj.goal_type === "session_create_events"
          ? { goal_type: "session_create_events", ...shared }
          : {
              goal_type: "create_events",
              ...shared,
              collection_enabled:
                typeof obj.collection_id === "string" &&
                obj.collection_id !== "",
            },
      );
    } else {
      passthroughGoals.push({ savedIndex, goal: obj });
    }
  }
  return { goals: parsed, passthroughGoals };
}
